// frontend/src/charts/semanticApi.js
// Client for the semantic API (backend app/semantic/render_router.py).
// Every call goes through apiCall, so the Bearer token and the auto-logout
// rules are the same as the rest of the app. Errors become SemanticError
// with the backend's typed code; raw server text is never shown to users.
// Phase 4 step 14a-3: dashboards (backend app/dashboards/routes.py) and the
// chart delete that a dashboard can refuse (CHART_IN_USE).
// Phase 4 step 14d-3: who can open a dashboard (backend app/dashboards/access_routes.py).
import { apiCall } from '../utils/api';

export class SemanticError extends Error {
  constructor(status, code, message, path = null, charts = [], calculations = [], pos = null, extra = {}) {
    super(message);
    this.name = 'SemanticError';
    this.status = status;
    this.code = code;
    this.path = path;
    this.charts = charts;               // FIELD_IN_USE / CALCULATION_IN_USE: [{ id, title }] of the charts affected
    this.calculations = calculations;   // 13c-3: [{ key, kind, name, display_name }] of the formulas affected
    this.pos = pos;                     // INVALID_CALCULATION: [start, end) into the formula when the formula is at fault
    this.dashboards = extra.dashboards || [];   // CHART_IN_USE (14a): [{ id, title }] of the dashboards showing the chart
    this.revision = extra.revision ?? null;     // DASHBOARD_CHANGED (14a): the dashboard's revision on the server now
    this.links = extra.links ?? null;           // PUBLIC_LINKS_ACTIVE (14d): how many working public links restricting would revoke
    this.user = extra.user ?? null;             // ALREADY_MEMBER (14d): { id, email, role } of the colleague the address belongs to
  }
}

/** ALREADY_MEMBER's colleague, kept only with the shape the dialog draws: { id: integer, email: text, role }. */
function cleanUser(u) {
  if (!u || !Number.isInteger(u.id) || u.id <= 0 || typeof u.email !== 'string' || !u.email) return null;
  return { id: u.id, email: u.email, role: typeof u.role === 'string' ? u.role : null };
}

const FALLBACK = {
  401: ['UNAUTHENTICATED', 'Your session has ended. Sign in again.'],
  403: ['FORBIDDEN', "You don't have access to this."],
  404: ['NOT_FOUND', 'This chart or dataset could not be found.'],
};

/** The charts a FIELD_IN_USE error lists, kept only when they have the shape we draw: { id: integer, title: text }. */
function cleanCharts(list) {
  if (!Array.isArray(list)) return [];
  return list
    .filter((c) => c && Number.isInteger(c.id) && c.id > 0)
    .map((c) => ({ id: c.id, title: typeof c.title === 'string' ? c.title : '' }));
}

/** The calculations an in-use error lists: { key, kind, name, display_name }, keys of the shape the server mints. */
export function cleanCalculations(list) {
  if (!Array.isArray(list)) return [];
  return list
    .filter((c) => c && typeof c.key === 'string' && CALC_KEY.test(c.key))
    .map((c) => ({
      key: c.key, kind: c.kind === 'measure' ? 'measure' : 'column',
      name: typeof c.name === 'string' ? c.name : c.key, display_name: typeof c.display_name === 'string' ? c.display_name : c.key,
    }));
}

/** A formula position [start, end) of non-negative integers, else null. */
export function cleanPos(pos) {
  if (!Array.isArray(pos) || pos.length !== 2) return null;
  const [a, b] = pos;
  if (!Number.isInteger(a) || !Number.isInteger(b) || a < 0 || b < a) return null;
  return [a, b];
}

export function toSemanticError(status, body) {
  const err = body && typeof body.detail === 'object' && body.detail ? body.detail.error : null;
  if (err && typeof err.code === 'string') {
    const extra = {
      dashboards: cleanCharts(err.dashboards),      // the same { id, title } shape
      revision: Number.isInteger(err.revision) && err.revision > 0 ? err.revision : null,
      links: Number.isInteger(err.links) && err.links >= 0 ? err.links : null,
      user: cleanUser(err.user),
    };
    return new SemanticError(status, err.code, err.message || 'Something went wrong.', err.path || null, cleanCharts(err.charts),
      cleanCalculations(err.calculations), cleanPos(err.pos), extra);
  }
  const [code, message] = FALLBACK[status] || ['HTTP_ERROR', 'Something went wrong. Try again.'];
  return new SemanticError(status, code, message);
}

function positiveId(id) {
  const n = Number(id);
  if (!Number.isInteger(n) || n <= 0) throw new SemanticError(400, 'BAD_ID', 'Invalid id.');
  return n;
}

async function call(endpoint, options = {}) {
  const res = await apiCall(endpoint, options);
  const body = res.status === 204 ? null : await res.json().catch(() => null);
  if (!res.ok) throw toSemanticError(res.status, body);
  return body;
}

export async function listDatasets(options = {}) {
  const body = await call('/semantic/datasets', options);
  return body.datasets || [];
}

export function getFields(datasetId, options = {}) {
  return call(`/semantic/datasets/${positiveId(datasetId)}/fields`, options);
}

export function getSpecSchema(options = {}) {
  return call('/semantic/spec-schema', options);
}

/** Run an unsaved spec (the builder's live preview). */
export function renderDraft(spec, { signal } = {}) {
  return call('/semantic/render', { method: 'POST', body: JSON.stringify({ spec }), signal });
}

/** Run a saved chart's stored spec. */
export function chartData(chartId, { signal } = {}) {
  return call(`/semantic/charts/${positiveId(chartId)}/data`, { signal });
}

/**
 * Phase 4 step 14b: a saved chart's data narrowed by a dashboard's filters. `filters` are
 * [{ field, op, value } | { target: 'time', op, value }] (filterState.requestFilters builds them);
 * the server ANDs them onto the stored spec and never lets them change anything else. The body is
 * chartData's plus `narrowing`: { applied: [{ index, field }], skipped: [{ index, reason }] }.
 */
export function chartDataFiltered(chartId, filters, { signal } = {}) {
  return call(`/semantic/charts/${positiveId(chartId)}/data`, {
    method: 'POST', body: JSON.stringify({ filters: filters || [] }), signal,
  });
}

/**
 * Phase 4 step 14b: distinct values of one field for a filter's list ({ values, truncated, ... }),
 * at most 200; `q` narrows text fields to values starting with it.
 */
export function fieldValues(datasetId, field, { q = '', signal } = {}) {
  const params = new URLSearchParams({ field: String(field) });
  if (q) params.set('q', q);
  return call(`/semantic/datasets/${positiveId(datasetId)}/field-values?${params.toString()}`, { signal });
}

// --- saved charts (Phase 3 step 9): the builder saves, loads and edits through /charts ---

/** A saved chart as the API serializes it (spec, chart_type, title, presentation, dataset_id, ...). */
export function getChart(chartId, { signal } = {}) {
  return call(`/charts/${positiveId(chartId)}`, { signal });
}

/** Create a chart from the builder's payload (savedChart.toSavePayload). Admins and analysts only. */
export function createChart(payload, { signal } = {}) {
  return call('/charts/', { method: 'POST', body: JSON.stringify(payload), signal });
}

/** Replace a saved chart's spec, type, title and presentation. */
export function updateChart(chartId, payload, { signal } = {}) {
  return call(`/charts/${positiveId(chartId)}`, { method: 'PUT', body: JSON.stringify(payload), signal });
}

/** Copy a saved chart (step 10c): same query and look, private, titled "(Copy)". Admins and analysts only. */
export function duplicateChart(chartId, { signal } = {}) {
  return call(`/charts/${positiveId(chartId)}/duplicate`, { method: 'POST', signal });
}

// --- converting charts from the previous builder (step 11) ---

/** What converting this chart would do: { status, spec, chart_type, notes, dropped, blockers }. Changes nothing. */
export function getConversionPlan(chartId, { signal } = {}) {
  return call(`/charts/${positiveId(chartId)}/conversion`, { signal });
}

/** Apply the plan (admins and analysts). Returns { chart, plan }. */
export function convertChart(chartId, { signal } = {}) {
  return call(`/charts/${positiveId(chartId)}/convert`, { method: 'POST', signal });
}

/** Put a converted chart back as it was (admins and analysts). Returns the chart. */
export function revertConversion(chartId, { signal } = {}) {
  return call(`/charts/${positiveId(chartId)}/revert-conversion`, { method: 'POST', signal });
}

/** Every chart of the organization still made with the previous builder, each with its plan. */
export function listConversions({ signal } = {}) {
  return call('/charts/conversions', { signal });
}

// --- the Model view (step 13): how a dataset's fields are classified ---

/** A field key goes in the URL path. Keys are raw names from the customer's data, so they are encoded; "." and ".." would be read as path steps by the browser, so they are refused. */
function fieldPath(key) {
  if (typeof key !== 'string' || key === '' || key === '.' || key === '..') {
    throw new SemanticError(400, 'BAD_KEY', "This field's name can't be changed here.");
  }
  return encodeURIComponent(key);
}

/** The dataset and every field (hidden ones too) with its role, defaults, the charts that use it, and can_edit. */
export function getModel(datasetId, { signal } = {}) {
  return call(`/semantic/datasets/${positiveId(datasetId)}/model`, { signal });
}

/** Change one field: any of role, default_aggregation, default_grain, display_name, hidden, force. Admins only. Returns the field. */
export function updateModelField(datasetId, key, patch, { signal } = {}) {
  return call(`/semantic/datasets/${positiveId(datasetId)}/fields/${fieldPath(key)}`, {
    method: 'PATCH', body: JSON.stringify(patch), signal,
  });
}

/** Change the dataset: display_name, is_certified. Admins only. Returns the dataset. */
export function updateModelDataset(datasetId, patch, { signal } = {}) {
  return call(`/semantic/datasets/${positiveId(datasetId)}`, { method: 'PATCH', body: JSON.stringify(patch), signal });
}

// --- calculated columns and measures (step 13c): a dataset's formulas ---

const CALC_KEY = /^_(calc|metric)_[a-z][a-z0-9_]{0,49}$/;   // the keys the server mints (calculations.py)

function calcPath(key) {
  if (typeof key !== 'string' || !CALC_KEY.test(key)) throw new SemanticError(400, 'BAD_KEY', 'Not a calculation of this dataset.');
  return key;
}

/** Every column and measure of the dataset (broken and disabled ones too), the function reference, limits, can_edit. */
export function listCalculations(datasetId, { signal } = {}) {
  return call(`/semantic/datasets/${positiveId(datasetId)}/calculations`, { signal });
}

/** Live validation for the editor: { kind, source, name?, key? } -> { ok: true, data_type, text, fields, uses } or { ok: false, error }. Never writes. */
export function checkCalculation(datasetId, body, { signal } = {}) {
  return call(`/semantic/datasets/${positiveId(datasetId)}/calculations/check`, { method: 'POST', body: JSON.stringify(body), signal });
}

/** Create: { kind, name, source, display_name?, description?, format?, null_policy? }. Admins only. Returns the calculation. */
export function createCalculation(datasetId, body, { signal } = {}) {
  return call(`/semantic/datasets/${positiveId(datasetId)}/calculations`, { method: 'POST', body: JSON.stringify(body), signal });
}

/** Change display_name / description / source / format / null_policy / is_enabled (+ force). Admins only. */
export function updateCalculation(datasetId, key, patch, { signal } = {}) {
  return call(`/semantic/datasets/${positiveId(datasetId)}/calculations/${calcPath(key)}`, {
    method: 'PATCH', body: JSON.stringify(patch), signal,
  });
}

/** Delete; `force` goes ahead even when charts or other formulas use it. Admins only. */
export function deleteCalculation(datasetId, key, { force = false, signal } = {}) {
  const query = force ? '?force=true' : '';
  return call(`/semantic/datasets/${positiveId(datasetId)}/calculations/${calcPath(key)}${query}`, { method: 'DELETE', signal });
}

// --- dashboards (Phase 4 step 14a): backend app/dashboards/routes.py, on the /semantic router ---

/** Every dashboard of the organization, newest change first: { can_edit, dashboards: [{ id, title, description, tile_count, ... }] }. */
export function listDashboards({ signal } = {}) {
  return call('/semantic/dashboards', { signal });
}

/** A dashboard with its tiles, each chart tile carrying a summary of its chart (null when the chart is gone), can_edit and the grid limits. */
export function getDashboard(dashboardId, { signal } = {}) {
  return call(`/semantic/dashboards/${positiveId(dashboardId)}`, { signal });
}

/** Admins and analysts: { title, description? } -> the new, empty dashboard (201). */
export function createDashboard(body, { signal } = {}) {
  return call('/semantic/dashboards', { method: 'POST', body: JSON.stringify(body), signal });
}

/**
 * Save: { revision, title?, description?, tiles? } (dashboardState.toSave builds it). The tile list replaces
 * the dashboard's as a whole. 409 DASHBOARD_CHANGED (err.revision = the server's) when someone saved in
 * between; 422 INVALID_DASHBOARD / CHART_NOT_FOUND with a path. Returns the saved dashboard.
 */
export function saveDashboard(dashboardId, body, { signal } = {}) {
  return call(`/semantic/dashboards/${positiveId(dashboardId)}`, { method: 'PUT', body: JSON.stringify(body), signal });
}

/** Count a view (any role); the page calls it once per open. Returns { view_count, last_viewed_at }. */
export function viewDashboard(dashboardId, { signal } = {}) {
  return call(`/semantic/dashboards/${positiveId(dashboardId)}/view`, { method: 'POST', signal });
}

/** Admins and analysts: a copy with every tile, titled "(copy)". Returns the copy. */
export function duplicateDashboard(dashboardId, { signal } = {}) {
  return call(`/semantic/dashboards/${positiveId(dashboardId)}/duplicate`, { method: 'POST', signal });
}

/** Admins and analysts. Resolves to null (204). */
export function deleteDashboard(dashboardId, { signal } = {}) {
  return call(`/semantic/dashboards/${positiveId(dashboardId)}`, { method: 'DELETE', signal });
}

// --- public links (Phase 4 step 14c): backend app/dashboards/shares.py, admins and analysts ---
// Each returns the whole listing (shareState.listingFromServer reads it); create and revoke add `share`,
// the link they touched. 409 SHARE_LIMIT at the cap of working links, 422 INVALID_SHARE with a path.

/** The dashboard's links, its saved filters and the charts a link shows. */
export function dashboardShares(dashboardId, { signal } = {}) {
  return call(`/semantic/dashboards/${positiveId(dashboardId)}/shares`, { signal });
}

/** { label?, expires_in_days? | expires_at?, allow_embed?, filter_ids? } -> the listing with `share` (201). */
export function createDashboardShare(dashboardId, body, { signal } = {}) {
  return call(`/semantic/dashboards/${positiveId(dashboardId)}/shares`, { method: 'POST', body: JSON.stringify(body || {}), signal });
}

/** Stop a link working (idempotent; the row stays, listed as revoked). */
export function revokeDashboardShare(dashboardId, shareId, { signal } = {}) {
  return call(`/semantic/dashboards/${positiveId(dashboardId)}/shares/${positiveId(shareId)}/revoke`, { method: 'POST', signal });
}

// --- who can open a dashboard (Phase 4 step 14d): backend app/dashboards/access_routes.py ---
// Admins, and analysts who may edit the dashboard. Each returns the whole listing (accessState.accessFromServer
// reads it); add, change, resend and remove add `person`, the row they touched, and add and resend say
// `person.mail_sent`. A dashboard the caller cannot open is 404; one they can open but not change is 403
// NO_EDIT_ACCESS.

function accessPath(dashboardId, accessId) {
  const base = `/semantic/dashboards/${positiveId(dashboardId)}/access`;
  return accessId === undefined ? base : `${base}/${positiveId(accessId)}`;
}

/** The level, the people named on the dashboard, its filters (for a guest's checklist) and the guest caps. */
export function dashboardAccess(dashboardId, { signal } = {}) {
  return call(accessPath(dashboardId), { signal });
}

/**
 * Set the level: 'org' or 'restricted'. Restricting a dashboard with working public links is refused with
 * 409 PUBLIC_LINKS_ACTIVE (err.links) unless `revokeLinks`, which revokes them for good.
 */
export function setDashboardAccess(dashboardId, access, { revokeLinks = false, signal } = {}) {
  const body = { access };
  if (revokeLinks) body.revoke_links = true;
  return call(accessPath(dashboardId), { method: 'PUT', body: JSON.stringify(body), signal });
}

/** The organization's active users whose email contains `q`, for the Add box: { people, truncated } (at most 50). */
export function dashboardPeople(dashboardId, q = '', { signal } = {}) {
  const params = new URLSearchParams({ q: String(q || '').slice(0, 100) });
  return call(`${accessPath(dashboardId)}/people?${params.toString()}`, { signal });
}

/**
 * Add one person: { kind: 'member', user_id, can_edit? } or { kind: 'guest', email, filter_ids }. 201 with
 * `person`. 422 ALREADY_MEMBER (err.user) when a guest's address is a colleague's; 409 ALREADY_SHARED, GUEST_LIMIT.
 */
export function addDashboardPerson(dashboardId, body, { signal } = {}) {
  return call(accessPath(dashboardId), { method: 'POST', body: JSON.stringify(body || {}), signal });
}

/** { can_edit } for a member, { filter_ids } for a guest. */
export function updateDashboardPerson(dashboardId, accessId, patch, { signal } = {}) {
  return call(accessPath(dashboardId, accessId), { method: 'PATCH', body: JSON.stringify(patch || {}), signal });
}

/** A guest: a new link, the invitation sent again (once per 15 minutes: 429 RESEND_LIMIT). */
export function resendDashboardInvite(dashboardId, accessId, { signal } = {}) {
  return call(`${accessPath(dashboardId, accessId)}/resend`, { method: 'POST', signal });
}

/** Take someone off the dashboard (idempotent; a guest's link stops working and their sessions end). */
export function removeDashboardPerson(dashboardId, accessId, { signal } = {}) {
  return call(`${accessPath(dashboardId, accessId)}/revoke`, { method: 'POST', signal });
}

/**
 * Delete a chart. A chart shown on a dashboard is refused with 409 CHART_IN_USE (err.dashboards lists them)
 * unless `force`, which leaves an empty tile on each of those dashboards. Resolves to null (204).
 */
export function deleteChart(chartId, { force = false, signal } = {}) {
  const query = force ? '?force=true' : '';
  return call(`/charts/${positiveId(chartId)}${query}`, { method: 'DELETE', signal });
}
